import { memo, useState, useEffect } from "react";
import { Modal, Tabs, Select, Divider } from "antd";
import styled from "styled-components";
import CollectItemsMapUtils from "../../../utils/CollectItemsMapUtils";
import {
  LayerStyles,
  LayerDisplayMode,
  defaultStyles,
} from "./LayerStyleModal/types";
import {
  PointStyleTab,
  LineStyleTab,
  PolygonStyleTab,
  LabelStyleTab,
  CollectorStyleTab,
  FormStyleTab,
  DateRainbowTab,
} from "./LayerStyleModal/tabs";

// Re-export types for backward compatibility
export type { LayerStyles, LayerDisplayMode };
export { defaultStyles };

interface LayerStyleModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (styles: LayerStyles) => void;
}

const LayerStyleModal = ({ open, onClose, onApply }: LayerStyleModalProps) => {
  const [styles, setStyles] = useState<LayerStyles>(defaultStyles);

  // Load current styles when modal opens
  useEffect(() => {
    if (open) {
      const currentStyles = CollectItemsMapUtils.getStyles();
      setStyles(currentStyles);
    }
  }, [open]);

  const handleApply = () => {
    onApply(styles);
    onClose();
  };

  const handleReset = () => {
    setStyles(defaultStyles);
  };

  const handleModeChange = (mode: LayerDisplayMode) => {
    setStyles((prev) => ({
      ...prev,
      mode,
    }));
  };

  const updateStyle = (category: keyof LayerStyles, field: string, value: any) => {
    setStyles((prev) => ({
      ...prev,
      [category]: {
        ...(prev[category] as any),
        [field]: value,
      },
    }));
  };

  const handleUpdateCollectorColor = (userId: number, color: string) => {
    setStyles((prev) => ({
      ...prev,
      collectorColors: {
        ...(prev.collectorColors || {}),
        [userId]: color,
      },
    }));
  };

  const handleResetCollectorColors = () => {
    setStyles((prev) => ({
      ...prev,
      collectorColors: {},
    }));
  };

  const handleUpdateFormColor = (formUuid: string, color: string) => {
    setStyles((prev) => ({
      ...prev,
      formColors: {
        ...(prev.formColors || {}),
        [formUuid]: color,
      },
    }));
  };

  const handleResetFormColors = () => {
    setStyles((prev) => ({
      ...prev,
      formColors: {},
    }));
  };

  const advancedTabs = [
    {
      key: 'point',
      label: 'Điểm',
      children: (
        <PointStyleTab
          styles={styles}
          onUpdate={(field, val) => updateStyle('point', field, val)}
        />
      ),
    },
    {
      key: 'line',
      label: 'Đường',
      children: (
        <LineStyleTab
          styles={styles}
          onUpdate={(field, val) => updateStyle('line', field, val)}
        />
      ),
    },
    {
      key: 'polygon',
      label: 'Vùng',
      children: (
        <PolygonStyleTab
          styles={styles}
          onUpdate={(field, val) => updateStyle('polygon', field, val)}
        />
      ),
    },
    {
      key: 'labels',
      label: 'Nhãn',
      children: (
        <LabelStyleTab
          styles={styles}
          onUpdate={(field, val) => updateStyle('labels', field, val)}
        />
      ),
    },
  ];

  return (
    <Modal
      title="Cài đặt hiển thị - Dữ liệu thu thập"
      open={open}
      onCancel={onClose}
      onOk={handleApply}
      okText="Áp dụng"
      cancelText="Hủy"
      width={620}
      destroyOnClose
      footer={[
        <FooterActions key="footer">
          <div>
            <ResetButton onClick={handleReset}>
              Đặt lại mặc định
            </ResetButton>
          </div>
          <div>
            <CancelButton onClick={onClose}>
              Hủy
            </CancelButton>
            <ApplyButton onClick={handleApply}>
              Áp dụng
            </ApplyButton>
          </div>
        </FooterActions>
      ]}
    >
      <ModalBody>
        <ModeSelectorSection>
          <ModeLabel>Chế độ hiển thị:</ModeLabel>
          <Select
            value={styles.mode || 'collector'}
            onChange={handleModeChange}
            style={{ width: '100%' }}
            options={[
              {
                value: 'collector',
                label: 'Hiển thị theo nhân viên thu thập (Mặc định)',
              },
              {
                value: 'form',
                label: 'Hiển thị theo biểu mẫu thu thập',
              },
              {
                value: 'date',
                label: 'Hiển thị theo ngày (Rainbow gradient)',
              },
              {
                value: 'advanced',
                label: 'Hiển thị nâng cao (Tùy chỉnh Điểm, Đường, Vùng, Nhãn)',
              },
            ]}
          />
        </ModeSelectorSection>

        <Divider style={{ margin: '12px 0' }} />

        {styles.mode === 'collector' && (
          <CollectorStyleTab
            styles={styles}
            onUpdateCollectorColor={handleUpdateCollectorColor}
            onResetCollectorColors={handleResetCollectorColors}
          />
        )}

        {styles.mode === 'form' && (
          <FormStyleTab
            styles={styles}
            onUpdateFormColor={handleUpdateFormColor}
            onResetFormColors={handleResetFormColors}
          />
        )}

        {styles.mode === 'date' && (
          <DateRainbowTab />
        )}

        {styles.mode === 'advanced' && (
          <Tabs
            items={advancedTabs}
            defaultActiveKey="point"
            size="small"
          />
        )}
      </ModalBody>
    </Modal>
  );
};

export default memo(LayerStyleModal);

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 380px;
`;

const ModeSelectorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
`;

const ModeLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const ResetButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const CancelButton = styled.button`
  padding: 6px 16px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  cursor: pointer;
  margin-right: 8px;
  font-size: 13px;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const ApplyButton = styled.button`
  padding: 6px 16px;
  border: 1px solid #3b82f6;
  background: #3b82f6;
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;

  &:hover {
    background: #2563eb;
    border-color: #2563eb;
  }
`;